import { Component, Inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DrawerService } from '../../services/drawer.service';
import { TranslationService } from '../../services/translation.service';
import { SupabaseUserToolsService } from '../../services/supabase-user-tools.service';
import { Router, RouterModule } from '@angular/router';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DOCUMENT } from '@angular/common';
import { ThemingService } from '../../services/theming.service';
import { ClubService } from '../../services/club.service';
import { PushNotificationService } from '../../services/push-notification.service';
import { UserService } from '../../services/user.service';
import { PwaService } from '../../services/pwa.service';
import { SponsorsService } from '../../../views/tournaments/components/sponsors-page/services/sponsors.service';
import { Sponsor } from '../../models/sponsor.models';
import { ReportIssueDialogComponent } from '../report-issue-dialog/report-issue-dialog.component';
import { MemberDietService } from '../../services/member-diet.service';
import { PermissionsService } from '../../services/permissions.service';

@Component({
  selector: 'app-drawer-menu',
  imports: [
    MatSidenavModule,
    TranslatePipe,
    MatListModule,
    MatIconModule,
    MatDialogModule,
    RouterModule,
  ],
  templateUrl: './drawer-menu.component.html',
  styleUrl: './drawer-menu.component.scss',
})
export class DrawerMenuComponent {
  @ViewChild('drawer')
  drawer!: MatSidenav;
  public lang = 'gr';
  title = 'Booking';
  public isUserAdmin = false;
  public isUserVerified = false;
  public userFullName: string = '';
  public userAvatarUrl: string | null = null;
  public hasDiscounts = false;

  constructor(
    protected drawerService: DrawerService,
    public router: Router,
    private translationService: TranslationService,
    private supabaseUserToolsService: SupabaseUserToolsService,
    protected clubsService: ClubService,
    protected themingService: ThemingService,
    private pushNotificationService: PushNotificationService,
    private userService: UserService,
    public pwaService: PwaService,
    private sponsorsService: SponsorsService,
    private dialog: MatDialog,
    public memberDiet: MemberDietService,
    public permissionsService: PermissionsService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.supabaseUserToolsService.isUserAdmin$
      .pipe(takeUntilDestroyed())
      .subscribe((isAdmin) => {
        this.isUserAdmin = isAdmin;
      });

    this.supabaseUserToolsService.isUserVerified$
      .pipe(takeUntilDestroyed())
      .subscribe((isVerified) => {
      this.isUserVerified = isVerified;
      if (this.isUserVerified) {
        this.loadUserProfile();
        this.checkDiscounts();
        // Always try to load the member's own diet plan when verified —
        // we used to gate this on `clubsService.flags()?.diets` but that
        // raced with the club-config load and silently skipped the call.
        // The RPC just returns null if no plan exists, no DB cost issue.
        void this.memberDiet.loadMyPlan();
        // Ask once, when the browser was never asked. A browser that already
        // granted push is subscribed silently (PushNotificationService
        // .syncSubscription on sign in).
        if (this.pushNotificationService.shouldOfferPush()) {
          try {
            navigator.serviceWorker.ready.then(() =>
              this.pushNotificationService.enablePushNotificationsPopup()
            );
          } catch (error) {
            console.error('Service Worker Ready Error:', error);
          }
        }
      }
    });
  }

  private async loadUserProfile() {
    const user = await this.userService.getUserPromise();
    if (user.data.user?.id) {
      const userInfo = await this.userService.getUserInfoFromUsersTable(
        user.data.user.id
      );
      if (userInfo.data && userInfo.data[0]) {
        this.userFullName = userInfo.data[0].fullname || '';
        this.userAvatarUrl = userInfo.data[0].avatar_url || null;
      }
    }
  }

  private async checkDiscounts(): Promise<void> {
    if (!this.clubsService.theme()?.show_sponsors) {
      this.hasDiscounts = false;
      return;
    }
    const { data } = await this.sponsorsService.getSponsors();
    const sponsors = (data as Sponsor[]) || [];
    this.hasDiscounts = sponsors.some(
      (s) => s.discount_description || s.discount_value
    );
  }

  openReportDialog(): void {
    this.drawer.close();
    this.dialog.open(ReportIssueDialogComponent, {
      panelClass: 'ri-dialog-panel',
      maxWidth: '100vw',
    });
  }

  switchLanguage(lang: string) {
    this.lang = lang;
    this.translationService.setLanguage(lang);
    localStorage.setItem('language', lang);
    this.document.documentElement.lang = lang === 'en' ? 'en' : 'el';
  }

  ngAfterViewInit() {
    this.drawerService.setDrawer(this.drawer);

    const savedLang = localStorage.getItem('language');
    if (savedLang) {
      this.switchLanguage(savedLang);
    } else {
      localStorage.setItem('language', 'gr');
      this.document.documentElement.lang = 'el';
    }
  }
}
